import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, Plus, Check, X, Trash2, MoreHorizontal, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CareerCard } from "./CareerCard";
import { OPP_TYPES, OPP_TYPE_LABELS } from "@/lib/careerConfig";
import { translateFr as tfr } from "@/i18n";

function OpportunityDialog({ open, onOpenChange, playerId, onChanged }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("club_interest");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/players/${playerId}/career-plan/opportunities`, {
        title: title.trim(), opportunity_type: type, reason: reason.trim(), source: "manual",
      });
      toast.success(tfr("Opportunité ajoutée"));
      onOpenChange(false);
      setTitle(""); setType("club_interest"); setReason("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tfr("Impossible d'ajouter l'opportunité")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="opportunity-dialog">
        <DialogHeader>
          <DialogTitle>{tfr("Nouvelle opportunité")}</DialogTitle>
          <DialogDescription>{tfr("Enregistrez une opportunité réelle identifiée pour cet athlète.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="opp-title">Titre</Label>
            <Input id="opp-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder={tfr("Ex. Intérêt du RC Strasbourg")} data-testid="opportunity-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="opportunity-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OPP_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="opp-reason">Contexte (optionnel)</Label>
            <Textarea id="opp-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={600} data-testid="opportunity-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tfr("Annuler")}</Button>
          <Button onClick={save} disabled={saving || !title.trim()} data-testid="opportunity-save-btn">{tfr("Ajouter")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OpportunitiesCard({ opportunities, suggestions, playerId, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const open = opportunities.filter((o) => o.status === "open");
  const closed = opportunities.filter((o) => o.status !== "open");
  const storedTitles = new Set(opportunities.map((o) => o.title));
  const freshSuggestions = (suggestions || []).filter((s) => !storedTitles.has(s.title));

  const patchStatus = async (oid, status, msg) => {
    try {
      await api.patch(`/players/${playerId}/career-plan/opportunities/${oid}`, { status });
      toast.success(msg);
      onChanged?.();
    } catch { toast.error(tfr("Impossible de mettre à jour l'opportunité")); }
  };
  const remove = async (oid) => {
    try {
      await api.delete(`/players/${playerId}/career-plan/opportunities/${oid}`);
      toast.success(tfr("Opportunité supprimée"));
      onChanged?.();
    } catch { toast.error(tfr("Impossible de supprimer l'opportunité")); }
  };
  const track = async (s) => {
    try {
      await api.post(`/players/${playerId}/career-plan/opportunities`, {
        title: s.title, opportunity_type: s.opportunity_type, reason: s.reason,
        source: "suggestion", relevance: s.relevance,
      });
      toast.success(tfr("Piste ajoutée au suivi"));
      onChanged?.();
    } catch { toast.error(tfr("Impossible d'ajouter la piste")); }
  };

  return (
    <CareerCard
      icon={Lightbulb}
      title={tfr("Opportunités")}
      subtitle={`${open.length} suivie${open.length > 1 ? "s" : ""}`}
      testid="opportunities-card"
      right={
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="h-8 gap-1 text-xs" data-testid="opportunity-add-btn">
          <Plus className="h-3.5 w-3.5" /> Ajouter
        </Button>
      }
    >
      {open.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-3" data-testid="opportunities-empty">
          {tfr("Aucune opportunité enregistrée pour le moment.")}
        </p>
      ) : (
        <ul className="space-y-2.5">
          {open.map((o) => (
            <li key={o.id} className="rounded-xl border border-border bg-secondary/30 p-3" data-testid={`opportunity-${o.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground/95">{o.title}</p>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-secondary text-muted-foreground border-border">
                      {OPP_TYPE_LABELS[o.opportunity_type] || "Autre"}
                    </Badge>
                    {o.source === "suggestion" && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-info/10 text-info border-info/30">Suggestion suivie</Badge>
                    )}
                  </div>
                  {o.reason && <p className="text-xs text-muted-foreground mt-1">{o.reason}</p>}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
                      aria-label={`${tfr("Actions sur l'opportunité")} ${o.title}`}
                      data-testid={`opportunity-menu-${o.id}`}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => patchStatus(o.id, "done", tfr("Opportunité concrétisée 🎉"))} data-testid={`opportunity-done-${o.id}`}>
                      <Check className="h-3.5 w-3.5 mr-2" /> Concrétisée
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => patchStatus(o.id, "dismissed", tfr("Opportunité écartée"))} data-testid={`opportunity-dismiss-${o.id}`}>
                      <X className="h-3.5 w-3.5 mr-2" /> Écarter
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => remove(o.id)} className="text-danger focus:text-danger" data-testid={`opportunity-delete-${o.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </li>
          ))}
        </ul>
      )}
      {closed.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-2">{closed.length} {closed.length > 1 ? tfr("traitées (concrétisées ou écartées)") : tfr("traitée (concrétisée ou écartée)")}</p>
      )}
      {freshSuggestions.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            <Sparkles className="h-3 w-3" aria-hidden="true" /> Pistes génériques — à vérifier
          </p>
          <ul className="space-y-2">
            {freshSuggestions.map((s) => (
              <li key={s.title} className="flex items-start justify-between gap-2" data-testid={`suggestion-${s.opportunity_type}`}>
                <div className="min-w-0">
                  <p className="text-sm text-foreground/85">{s.title}</p>
                  <p className="text-[11px] text-muted-foreground">{s.reason}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => track(s)} className="h-7 gap-1 text-xs shrink-0" data-testid={`suggestion-track-${s.opportunity_type}`}>
                  <Plus className="h-3 w-3" /> Suivre
                </Button>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-muted-foreground mt-2">
            {tfr("Repères de marché génériques (calendrier) — pas des opportunités vérifiées.")}
          </p>
        </div>
      )}
      <OpportunityDialog open={addOpen} onOpenChange={setAddOpen} playerId={playerId} onChanged={onChanged} />
    </CareerCard>
  );
}
